import React, { useState, useEffect } from 'react';
import { X, Sparkles, QrCode, Search, BookOpen, Activity, Droplets, Utensils, History, ShieldCheck, ChevronRight, Dumbbell } from 'lucide-react';
import { Meal, Symptom } from '../lib/types';

import { BarcodeScannerModal } from './BarcodeScannerModal';
import { Sheet } from './Sheet';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenMealLogger?: (initialTab?: 'search' | 'scan' | 'ai' | 'library') => void;
  onSelectAiPhoto: () => void;
  onSelectDescribe: () => void;
  onSelectQuickMeal: (meal: Meal) => void;
  onOpenSymptom: () => void;
  onOpenPoopLog: () => void;
  onOpenExercise?: () => void;
  onBarcodeProductSelected: (product: { name: string; ingredients: string[] }) => void;
  onOpenFodmapScanner?: () => void;
  recentMeals: Meal[];
}

export const QuickAddModal: React.FC<QuickAddModalProps> = ({
  isOpen,
  onClose,
  onOpenMealLogger,
  onSelectAiPhoto,
  onSelectDescribe,
  onSelectQuickMeal,
  onOpenSymptom,
  onOpenPoopLog,
  onOpenExercise,
  onBarcodeProductSelected,
  onOpenFodmapScanner,
  recentMeals,
}) => {
  const [isBarcodeOpen, setIsBarcodeOpen] = useState(false);
  const [showRecentPicker, setShowRecentPicker] = useState(false);

  // Reset internal sheet state every time QuickAdd opens
  useEffect(() => {
    if (isOpen) {
      setShowRecentPicker(false);
      setIsBarcodeOpen(false);
    }
  }, [isOpen]);

  // Deduplicate unique recent meal descriptions
  const uniqueRecentMeals = Array.from(
    new Map(recentMeals.map((m) => [m.description.toLowerCase(), m])).values()
  ).slice(0, 8);

  return (
    <Sheet isOpen={isOpen} onClose={onClose} ariaLabel="Quick Add">

        {/* Sheet Header */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-200/60 dark:border-zinc-800">
          <h2 className="text-xl font-black uppercase tracking-tight">Quick Add</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:text-rose-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 overflow-y-auto overscroll-contain min-h-0">
          
          {/* Tier 1: Low FODMAP Scanner & Food Checker */}
          <button
            type="button"
            onClick={() => {
              onClose();
              onOpenFodmapScanner?.();
            }}
            className="w-full p-3.5 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/5 hover:from-emerald-500/20 hover:to-teal-500/15 border border-emerald-500/30 text-slate-900 dark:text-white flex items-center justify-between group active:scale-[0.99] transition-all shadow-xs"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-md shadow-emerald-500/20 shrink-0">
                <ShieldCheck className="w-5 h-5 stroke-[2.5]" />
              </div>
              <div className="text-left">
                <div className="text-xs font-black uppercase tracking-tight flex items-center space-x-1.5">
                  <span>Low FODMAP Scanner</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-bold">
                    NEW
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-zinc-400 font-medium">
                  Scan barcode, photo, or test recipe safety
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-emerald-600 dark:text-emerald-400 group-hover:translate-x-0.5 transition-transform shrink-0" />
          </button>

          {/* Tier 2: 4 UNIFIED MEAL LOGGING OPTIONS (Search, Scan, AI, Library) */}
          <div className="p-4 rounded-3xl bg-white dark:bg-[#1A1D24] border border-slate-200/80 dark:border-zinc-800 shadow-xs space-y-3">
            <span className="text-[11px] font-black tracking-widest text-slate-400 dark:text-zinc-500 uppercase text-center block">
              MEAL LOGGING OPTIONS
            </span>

            <div className="grid grid-cols-4 gap-2 pt-1 justify-items-center">
              {/* 1. Search */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenMealLogger ? onOpenMealLogger('search') : onSelectDescribe();
                }}
                className="flex flex-col items-center space-y-1.5 group w-full"
              >
                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl sm:rounded-3xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 group-hover:border-black dark:group-hover:border-white flex items-center justify-center text-slate-900 dark:text-white shadow-xs active:scale-95 transition-all">
                  <Search className="w-5 h-5 stroke-[2]" />
                </div>
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">Search</span>
              </button>

              {/* 2. Scan */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenMealLogger ? onOpenMealLogger('scan') : setIsBarcodeOpen(true);
                }}
                className="flex flex-col items-center space-y-1.5 group w-full"
              >
                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl sm:rounded-3xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 group-hover:border-black dark:group-hover:border-white flex items-center justify-center text-slate-900 dark:text-white shadow-xs active:scale-95 transition-all">
                  <QrCode className="w-5 h-5 stroke-[2]" />
                </div>
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">Scan</span>
              </button>

              {/* 3. AI */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenMealLogger ? onOpenMealLogger('ai') : onSelectAiPhoto();
                }}
                className="flex flex-col items-center space-y-1.5 group w-full"
              >
                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl sm:rounded-3xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 group-hover:border-black dark:group-hover:border-white flex items-center justify-center text-slate-900 dark:text-white shadow-xs active:scale-95 transition-all">
                  <Sparkles className="w-5 h-5 stroke-[2]" />
                </div>
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">AI</span>
              </button>

              {/* 4. Library */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenMealLogger ? onOpenMealLogger('library') : setShowRecentPicker(!showRecentPicker);
                }}
                className="flex flex-col items-center space-y-1.5 group w-full"
              >
                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl sm:rounded-3xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 group-hover:border-black dark:group-hover:border-white flex items-center justify-center text-slate-900 dark:text-white shadow-xs active:scale-95 transition-all">
                  <BookOpen className="w-5 h-5 stroke-[2]" />
                </div>
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">Library</span>
              </button>
            </div>
          </div>

          {/* Quick Recent Meals Accordion */}
          {showRecentPicker && (
            <div className="p-4 bg-white dark:bg-[#1A1D24] rounded-3xl border border-slate-200 dark:border-zinc-800 space-y-2 animate-fade-in">
              <div className="flex items-center space-x-2 text-xs font-black uppercase tracking-wider text-slate-500">
                <History className="w-3.5 h-3.5" />
                <span>Repeat Recent Meal</span>
              </div>
              {uniqueRecentMeals.length > 0 ? (
                <div className="space-y-1.5 divide-y divide-slate-100 dark:divide-zinc-800 max-h-48 overflow-y-auto">
                  {uniqueRecentMeals.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        onSelectQuickMeal(m);
                        onClose();
                      }}
                      className="w-full py-2 text-left flex items-center justify-between hover:text-emerald-600 dark:hover:text-emerald-400 active:scale-[0.99] transition-all"
                    >
                      <div className="min-w-0 pr-2">
                        <span className="text-xs font-bold block truncate text-slate-900 dark:text-white">{m.description}</span>
                        <span className="text-[10px] text-slate-400 font-medium">{m.meal_type}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-400 shrink-0">+ Log</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No previous meals logged yet.</p>
              )}
            </div>
          )}

          {/* Tier 3: Three Action Buttons (Symptom, Exercise, Log Your Log) */}
          <div className="grid grid-cols-3 gap-2.5 pt-1">
            {/* SYMPTOM Button */}
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenSymptom();
              }}
              className="py-3.5 px-2 rounded-2xl bg-white dark:bg-[#1A1D24] hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-900 dark:text-white font-black text-[11px] uppercase tracking-wider shadow-xs active:scale-95 transition-all border border-slate-200 dark:border-zinc-800 flex flex-col items-center justify-center space-y-1.5"
            >
              <Activity className="w-4 h-4 text-rose-500" />
              <span>SYMPTOM</span>
            </button>

            {/* EXERCISE Button */}
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenExercise?.();
              }}
              className="py-3.5 px-2 rounded-2xl bg-white dark:bg-[#1A1D24] hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-900 dark:text-white font-black text-[11px] uppercase tracking-wider shadow-xs active:scale-95 transition-all border border-slate-200 dark:border-zinc-800 flex flex-col items-center justify-center space-y-1.5"
            >
              <Dumbbell className="w-4 h-4 text-teal-500" />
              <span>EXERCISE</span>
            </button>

            {/* LOG YOUR LOG (Poop) Button */}
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenPoopLog();
              }}
              className="py-3.5 px-2 rounded-2xl bg-white dark:bg-[#1A1D24] hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-900 dark:text-white font-black text-[11px] uppercase tracking-wider shadow-xs active:scale-95 transition-all border border-slate-200 dark:border-zinc-800 flex flex-col items-center justify-center space-y-1.5"
            >
              <Droplets className="w-4 h-4 text-amber-500" />
              <span>LOG YOUR LOG</span>
            </button>
          </div>

        </div>

      {/* Embedded Barcode Scanner Dialog */}
      <React.Suspense fallback={null}>
        {isBarcodeOpen && (
          <BarcodeScannerModal
            isOpen={isBarcodeOpen}
            onClose={() => setIsBarcodeOpen(false)}
            onSelectProduct={(prod) => {
              setIsBarcodeOpen(false);
              onClose();
              onBarcodeProductSelected(prod);
            }}
          />
        )}
      </React.Suspense>
    </Sheet>
  );
};
